import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import IncentiveTracker from '../incentives/IncentiveTracker';
import BannerMessage from '../banner/BannerMessage';
import UnsubscribeBanner from '../banner/UnsubscribeBanner';
import ChromeFoldContext from './ChromeFoldContext';
import { selectIncentiveProductsToTrack } from '../../../selectors';

// True when IncentiveTracker has at least one row to render. This MUST read
// the same selector IncentiveTracker's own guard reads: the two used to
// duplicate the formula, and ENG-2965 widened the tracker's side to include
// cart-level incentives stashed on other orders in the cart without widening
// this one. The divergence left the fixed 50px tracker rendering over page
// content that reserved no space for it, and — since ENG-2551 — left
// `--chrome-offset` at 52px instead of 104px, so the folded category picker
// and every fold scroll target landed underneath the tracker.
const useIncentiveTrackerVisible = () => {
  const selectedStudent = useSelector(
    (state) => state.additionalData?.selectedStudent,
  );
  const productsToTrack = useSelector(selectIncentiveProductsToTrack);
  return !!selectedStudent && productsToTrack.length > 0;
};

const V3Layout = ({ children, showStudentBanner }) => {
  const loading = 'hidden';
  const trackerVisible = useIncentiveTrackerVisible();
  // ENG-2551: an ordering step can fold the chrome (slim tracker + reduced
  // reserved margin) so its content owns the mobile viewport.
  const [chromeFolded, setChromeFolded] = useState(false);
  const contextValue = useMemo(
    () => ({ folded: chromeFolded, setFolded: setChromeFolded }),
    [chromeFolded],
  );

  let containerClass = 'order-container';
  if (trackerVisible) {
    containerClass += chromeFolded
      ? ' margin-for-incentive-tracker--folded'
      : ' margin-for-incentive-tracker';
  }

  return (
    <ChromeFoldContext.Provider value={contextValue}>
      <div className={containerClass}>
        <div className={loading}>
          <div className="spinner" />
        </div>
        <IncentiveTracker />
        <UnsubscribeBanner />
        <BannerMessage showStudentBanner={showStudentBanner} />
        {children}
      </div>
    </ChromeFoldContext.Provider>
  );
};

export default V3Layout;
